import { useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';

/** A v5-style location descriptor: either a path string or a partial location object. */
type LocationDescriptor = string | { pathname?: string; search?: string; hash?: string; state?: unknown };

const toNavigateTarget = (to: LocationDescriptor) =>
  typeof to === 'string' ? to : { pathname: to.pathname, search: to.search, hash: to.hash };

const toNavigateState = (to: LocationDescriptor, state?: unknown) =>
  typeof to === 'string' ? state : to.state ?? state;

/**
 * A react-router v5 `history` object, reconstructed over v6 primitives so that
 * pre-v6 call sites (`history.push(...)`, `history.replace(...)`, `history.goBack()`,
 * `history.location`) keep working unchanged while the app runs on react-router v6.
 */
export interface CompatHistory {
  push(to: LocationDescriptor, state?: unknown): void;
  replace(to: LocationDescriptor, state?: unknown): void;
  goBack(): void;
  goForward(): void;
  go(delta: number): void;
  location: Location;
}

/**
 * Drop-in replacement for react-router v5's `useHistory`, backed by v6's
 * `useNavigate` + `useLocation`. Preserves the v5 call shapes so the bulk of the
 * codebase can migrate to v6 without rewriting every navigation call site.
 *
 * IDENTITY IS LOAD-BEARING: v5's history was a stable singleton, and consumers
 * keep `history` in useEffect/useCallback dependency arrays. The returned object
 * is therefore memoized on `navigate` only (stable per router), and `location`
 * is exposed through a ref-backed getter so reads are always live — matching
 * v5's mutable `history.location` — without changing the object's identity on
 * every navigation (which turned unconditional `history.replace` effects into
 * infinite loops).
 */
export const useHistory = (): CompatHistory => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  return useMemo<CompatHistory>(
    () => ({
      push: (to, state) => navigate(toNavigateTarget(to), { state: toNavigateState(to, state) }),
      replace: (to, state) =>
        navigate(toNavigateTarget(to), { replace: true, state: toNavigateState(to, state) }),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      go: (delta) => navigate(delta),
      get location() {
        return locationRef.current;
      },
    }),
    [navigate],
  );
};
